<!-- Recent Projects Section -->
@if (recentProjects().length > 0) {
  <section class="rounded-2xl border border-slate-200 bg-white shadow-sm mb-4">
    <div class="card-header">
      <h2 class="section-header-title">Group Projects ({{ projectCount() }})</h2>
      <div class="flex items-center gap-2">
        <button
          type="button"
          (click)="viewGroupProjects()"
          class="text-sm font-semibold text-secondary hover:underline mr-4"
        >
          View all →
        </button>
        @if (!isViewer()) {
          <button type="button" (click)="openProjectCreateModal()" class="btn-primary">
            Create Project
          </button>
        }
      </div>
    </div>
    <div class="space-y-0">
      @for (project of recentProjects(); track project.id) {
        <app-project-card
          class="border-b border-slate-200 last:border-0 px-2"
          [project]="project"
          [isInline]="true"
        ></app-project-card>
      }
    </div>
  </section>
} @else {
  <section class="rounded-2xl border border-slate-200 bg-white shadow-sm mb-4">
    <div class="card-header">
      <h2 class="section-header-title">Projects</h2>
      @if (!isViewer()) {
        <button type="button" (click)="openProjectCreateModal()" class="btn-primary">
          Create Project
        </button>
      }
    </div>
    <div class="px-6 py-10 text-center">
      <p class="text-gray-600">
        @if (isViewer()) {
          As a viewer you have read-only access to this group.
        } @else {
          No projects in this group yet. Create one to get started.
        }
      </p>
    </div>
  </section>
}

<!-- Members Section -->
@if (!isViewer()) {
  <section class="rounded-2xl border border-slate-200 bg-white shadow-sm">
    <div class="card-header">
      <h2 class="section-header-title">Members ({{ totalMembers() }})</h2>
      <div class="max-w-102 w-full pl-2">
        <input
          type="search"
          class="input-field w-full"
          placeholder="Search members by name or email…"
          [value]="searchQuery()"
          (input)="onSearchChange($any($event.target).value)"
        />
      </div>
    </div>

    @if (isOwner() && joinRequests().length > 0) {
      <button
        type="button"
        (click)="showRequestsModal.set(true)"
        class="w-full border-b border-slate-200 px-6 py-3 flex items-center justify-between bg-amber-50/50 hover:bg-amber-100/50 transition-colors group"
      >
        <span class="text-sm text-amber-800 font-medium">
          {{ joinRequests().length }} pending request{{ joinRequests().length === 1 ? '' : 's' }}
        </span>
        <span class="text-sm font-semibold text-amber-700 group-hover:underline">Review →</span>
      </button>
    }

    @if (loading() && membersView().length === 0) {
      <div class="px-6 py-10 text-center text-gray-600">Loading members…</div>
    } @else if (totalMembers() === 0) {
      <div class="px-6 py-10 text-center">
        @if (searchQuery().length > 0) {
          <p class="text-gray-600">No members match your search.</p>
        } @else {
          <p class="text-gray-600">No members in this group yet.</p>
        }
      </div>
    } @else {
      <div class="divide-y divide-slate-200">
        @for (member of membersView(); track member.user.id) {
          <div class="list-item">
            <div class="min-w-0 flex-1">
              <div class="flex items-center gap-2">
                <p class="font-medium text-gray-900">
                  {{ member.user.firstName }} {{ member.user.lastName }}
                </p>
                <span class="badge-primary">
                  {{
                    member.groupRole
                      .split('_')
                      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                      .join(' ')
                  }}
                </span>
              </div>
              <div class="flex items-center gap-2">
                <p class="text-sm text-gray-600">{{ member.user.email }}</p>
                <span class="text-xs text-gray-400">•</span>
                <p class="text-xs text-gray-500">
                  Joined {{ member.joinedAt | date: 'MMM d, yyyy' }}
                </p>
              </div>
            </div>

            @if (member.groupRole !== 'OWNER' && isOwner()) {
              <button
                type="button"
                (click)="askRemove(member)"
                [disabled]="removingMemberId() === member.user.id"
                class="btn-small-danger"
              >
                {{ removingMemberId() === member.user.id ? 'Removing…' : 'Remove' }}
              </button>
            }
          </div>
        }
      </div>

      @if (totalPages() > 1) {
        <div class="flex items-center justify-between px-6 py-3 text-sm text-gray-600">
          <button
            type="button"
            (click)="goToPage(currentPage() - 1)"
            [disabled]="!canPrev()"
            class="btn-small-secondary disabled:cursor-not-allowed disabled:opacity-40"
          >
            « Prev
          </button>
          <span>Page {{ currentPage() + 1 }} of {{ totalPages() }}</span>
          <button
            type="button"
            (click)="goToPage(currentPage() + 1)"
            [disabled]="!canNext()"
            class="btn-small-secondary disabled:cursor-not-allowed disabled:opacity-40"
          >
            Next »
          </button>
        </div>
      }
    }
  </section>
} @else if (!loading()) {
  <section class="rounded-2xl border border-slate-200 bg-white shadow-sm">
    <div class="card-header">
      <h2 class="section-header-title">Members</h2>
    </div>
    <div class="px-6 py-10 text-center">
      <p class="text-gray-600">
        As a viewer you have read-only access to this group. The members list is only visible to
        group members.
      </p>
    </div>
  </section>
}

<!-- Pending Requests Modal -->
<app-base-modal
  [open]="showRequestsModal()"
  (openChange)="showRequestsModal.set($event)"
  title="Pending Join Requests"
  size="md"
>
  <div class="space-y-4 max-h-124 overflow-y-auto">
    @if (joinRequests().length === 0) {
      <div class="text-center py-10">
        <p class="text-gray-600">No pending requests.</p>
      </div>
    } @else {
      <div class="divide-y divide-slate-200 -mt-2">
        @for (request of joinRequests(); track request.id) {
          <div class="px-0 py-2">
            <div class="mb-2 text-sm">
              <p class="font-medium text-gray-900 text-base">{{ request.userName }}</p>
              <p class="text-gray-600">
                {{ request.email }} · {{ request.createdAt | date: "MMM d, yyyy 'at' h:mm a" }}
              </p>
            </div>
            <div class="flex gap-1">
              <button
                type="button"
                (click)="approveRequest(request)"
                [disabled]="processingRequestId() === request.id"
                class="flex-1 rounded-md bg-secondary px-2 py-1 text-xs font-semibold text-white transition-colors hover:bg-hover disabled:cursor-not-allowed disabled:opacity-70"
              >
                {{ processingRequestId() === request.id ? 'Processing…' : 'Accept' }}
              </button>
              <button
                type="button"
                (click)="rejectRequest(request)"
                [disabled]="processingRequestId() === request.id"
                class="flex-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-70"
              >
                Decline
              </button>
            </div>
          </div>
        }
      </div>
    }
  </div>
</app-base-modal>

<app-confirm-modal
  [open]="showRemoveConfirm()"
  title="Remove Member"
  [message]="'The user ' + memberToRemove()?.user?.firstName + ' will be removed from the group.'"
  confirmText="Remove Member"
  cancelText="Cancel"
  [confirmVariant]="'danger'"
  [loading]="deleting()"
  size="sm"
  (openChange)="showRemoveConfirm.set($event)"
  (confirmed)="removeMember()"
  (canceled)="cancelRemove()"
></app-confirm-modal>

<app-project-create-modal
  [open]="showProjectCreateModal()"
  [preselectedGroupId]="groupId ? groupId : null"
  (openChange)="showProjectCreateModal.set($event)"
  (created)="onProjectCreated($event)"
></app-project-create-modal>
